'use client';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { blogApi } from '@/lib/api';
import { BlogPost } from '@/types';

export default function BlogPostPage() {
  const params = useParams();
  const slug = params.slug as string;
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    blogApi.getOne(slug).then(data => {
      setPost(data);
      setLoading(false);
    }).catch(() => {
      // Use placeholder if API fails
      if (slug === 'getting-started-drf') {
        setPost({ id: 1, title: 'Getting Started with Django REST Framework', slug: 'getting-started-drf', excerpt: 'Learn how to build powerful REST APIs...', content: '# Introduction\n\nDjango REST Framework is powerful...', cover_image: null, tags: ['Django', 'Python', 'API'], published_at: '2024-05-15T10:00:00Z', read_time: 5 });
      }
      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading post...</div>;
  }

  if (!post) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>Post not found</h1>
        <Link href="/blog" className="btn btn-outline">Back to Blog</Link>
      </div>
    );
  }

  return (
    <article style={{ paddingTop: '8rem', minHeight: '100vh', paddingBottom: '4rem' }}>
      <div className="container" style={{ maxWidth: '800px' }}>
        <Link href="/blog" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', marginBottom: '2.5rem', fontFamily: 'Outfit, sans-serif' }}>
          <ArrowLeft size={16} /> Back to all posts
        </Link>
        
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
            {post.tags.map(tag => <span key={tag} className="tag">{tag}</span>)}
          </div>
          
          <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '1.5rem', lineHeight: 1.2 }}>
            {post.title}
          </h1>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', color: 'var(--text-subtle)', marginBottom: '3rem', fontFamily: 'Outfit, sans-serif' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={16} />
              {new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={16} />
              {post.read_time} min read
            </div>
          </div>
        </motion.div>

        {post.cover_image && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} style={{ width: '100%', height: 'auto', aspectRatio: '16/9', borderRadius: '12px', overflow: 'hidden', marginBottom: '3rem', border: '1px solid var(--border)' }}>
            <Image src={post.cover_image} alt={post.title} width={800} height={450} style={{ objectFit: 'cover', width: '100%', height: '100%' }} priority />
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }} style={{ color: 'var(--text-muted)', lineHeight: 1.8, fontSize: '1.1rem' }}>
          {/* Note: In a real app, you'd use a markdown renderer here */}
          <div dangerouslySetInnerHTML={{ __html: post.content?.replace(/\n/g, '<br/>') || post.excerpt }} />
        </motion.div>
      </div>
    </article>
  );
}
